// React, Next
import { useCallback, useEffect, useRef, useState } from 'react'
import Image, { StaticImageData } from 'next/image'

// MUI
import Box from '@mui/material/Box'
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew'
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos'

// Embla
import useEmblaCarousel from 'embla-carousel-react'
import Autoplay from 'embla-carousel-autoplay'

// Style
import themeColors from '@/assets/theme/colors'
import classes from './ImageCarrousel-CSS'

type ButtonProps = {
  enabled: boolean
  side: 'left' | 'right'
  onClick: () => void
}

function ArrowButton(p: ButtonProps) {
  if (!p.enabled) return null
  return (
    <Box onClick={p.onClick} sx={{ ...classes.emblaButton, [p.side]: '8px' }}>
      {p.side === 'left' ? (
        <ArrowBackIosNewIcon
          sx={{ ...classes.arrowButtonColor, transform: 'scale(0.7)' }}
        />
      ) : (
        <ArrowForwardIosIcon
          sx={{ ...classes.arrowButtonColor, transform: 'scale(0.7)' }}
        />
      )}
    </Box>
  )
}

type Props = {
  images: StaticImageData[]
  /** Alt text per image, in the same order. Omit for decorative sets. */
  alts?: string[]
  /** Aspect ratio of the frame every image is fitted into. */
  ratio?: string
  /** Autoplay interval in ms. Pass 0 to leave the carousel manual. */
  autoplayDelay?: number
}

/**
 * One image per slide, fitted rather than cropped: the sources here vary from
 * portrait screenshots to wide charts, so a shared frame with `contain` keeps
 * every one of them whole.
 */
export default function ImageCarrousel(props: Props) {
  const autoplay = useRef(
    Autoplay({ delay: props.autoplayDelay ?? 7000, stopOnInteraction: false })
  )

  const [viewportRef, embla] = useEmblaCarousel(
    { align: 'start', skipSnaps: false, loop: true },
    props.autoplayDelay === 0 ? [] : [autoplay.current]
  )

  const [prevEnabled, setPrevEnabled] = useState(false)
  const [nextEnabled, setNextEnabled] = useState(false)
  const [selected, setSelected] = useState(0)

  const scrollPrev = useCallback(() => {
    if (!embla) return
    embla.scrollPrev()
    autoplay.current.reset()
  }, [embla])

  const scrollNext = useCallback(() => {
    if (!embla) return
    embla.scrollNext()
    autoplay.current.reset()
  }, [embla])

  const scrollTo = useCallback(
    (index: number) => {
      if (!embla) return
      embla.scrollTo(index)
      autoplay.current.reset()
    },
    [embla]
  )

  const onSelect = useCallback(() => {
    if (!embla) return
    setPrevEnabled(embla.canScrollPrev())
    setNextEnabled(embla.canScrollNext())
    setSelected(embla.selectedScrollSnap())
  }, [embla])

  useEffect(() => {
    if (!embla) return
    embla.on('select', onSelect)
    embla.on('reInit', onSelect)
    onSelect()
  }, [embla, onSelect])

  // *** MAIN RENDER ***
  return (
    <Box sx={classes.embla}>
      <ArrowButton side="left" enabled={prevEnabled} onClick={scrollPrev} />
      <ArrowButton side="right" enabled={nextEnabled} onClick={scrollNext} />

      <Box sx={classes.emblaViewport} ref={viewportRef}>
        <Box sx={classes.emblaContainer}>
          {props.images.map((image, i) => (
            <Box key={i} sx={classes.emblaSlide}>
              <Box
                sx={{
                  position: 'relative',
                  width: '100%',
                  aspectRatio: props.ratio ?? '4 / 3',
                  background: themeColors.section.light
                }}
              >
                <Image
                  src={image}
                  alt={props.alts?.[i] ?? ''}
                  fill
                  sizes="(max-width: 990px) 100vw, 40vw"
                  // Fitted, never cropped.
                  style={{ objectFit: 'contain' }}
                />
              </Box>
            </Box>
          ))}
        </Box>
      </Box>

      <Box sx={classes.dots}>
        {props.images.map((image, i) => (
          <Box
            key={i}
            onClick={() => scrollTo(i)}
            sx={{
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              cursor: 'pointer',
              background:
                i === selected
                  ? themeColors.success.main
                  : themeColors.gray.lighter
            }}
          />
        ))}
      </Box>
    </Box>
  )
}
